import AuthenticationService from "service/auth/AuthenticationService";
import EnvironmentService from "service/environment/EnvironmentService";
import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";
import DoctorCategory from "constants/type/user/DoctorCategory";

/** GET /v2/tenant lists 1 "Zyla" / 2 "Novartis". phr serves Zyla. */
const TENANT_ZYLA = 1;

class UserService {
  constructor() {
    this.authenticationService = new AuthenticationService();
    this.environmentService = new EnvironmentService();
    this.apiService = new APIService();
  }

  /**
   * Login is the DOCTOR flow — the same endpoint pair expert-dashboard uses,
   * so a phr session carries who="doctor" and the doctor role set (role 4, via
   * api-user-service core/user.go CreateDefaultDoctorRoles). That role set is
   * what the clinical APIs check, and its absence is why call-note writes came
   * back 401. The employee pair mints who="employee" with roles 2+3 instead.
   *
   * TWO CONSEQUENCES, both verified against api-user-service and accepted:
   *
   *   · A NUMBER WITH NO ACTIVE DOCTOR RECORD IS REFUSED OUTRIGHT.
   *     DoctorLoginController.GetOTP looks the doctor up by phone and returns
   *     403 "No record found. Please contact the admin for account creation"
   *     before any SMS is sent. phr is a doctors-only app from here on;
   *     employee-only staff cannot sign in at all.
   *
   *   · THE RESPONSE CARRIES EmployeeProfile: nil — doctor_login.go sets it
   *     unconditionally. Everything downstream must therefore read
   *     doctorProfile. AuthenticationService already falls back to it for the
   *     name, avatar and gender; newAPIService.initializeHeaders consequently
   *     sends DOCTOR_CLIENT, which is the right client for this token; and
   *     UserService.isEmployee() below now answers false for everyone.
   *
   * tenantid 1 = "Zyla" (verified against GET /v2/tenant, which lists
   * 1 Zyla / 2 Novartis). Bridge resolves it by name from that endpoint and
   * offers a picker; phr serves Zyla only, so it is pinned here.
   */
  getOTP(cc, phoneNo, onResponse, onError) {
    let url =
      APIConfig.user.getDoctorOTP +
      "?phoneno=" +
      phoneNo +
      "&countrycode=" +
      cc +
      "&tenantid=" +
      TENANT_ZYLA;

    return this.apiService.get(url, onResponse, onError);
  }

  verifyOTP(countrycode, phoneNo, otp, tenantId, onResponse, onError) {
    let url = APIConfig.user.verifyDoctorOTP;
    let data = {
      countrycode: parseInt(countrycode),
      phoneno: parseInt(phoneNo),
      otp: parseInt(otp),
      tenantid: TENANT_ZYLA,
    };

    return this.apiService.post(url, data, onResponse, onError);
  }
  // MFA step 2. verifyOTP above returns an mfaStatus challenge carrying a
  // partialToken and a maskedEmail hint instead of a session; this exchanges
  // that token plus the emailed code for the real EmployeeVerifyResponse
  // (accessToken populated, mfaStatus absent).
  verifyDoctorEmailOtp(partialToken, otp, onResponse, onError) {
    let url = APIConfig.user.verifyDoctorEmailOtp;
    let data = {
      partialToken: partialToken,
      otp: parseInt(otp),
    };

    return this.apiService.post(url, data, onResponse, onError);
  }

  getDoctorOTP(phoneNo, onResponse, onError) {
    let url = APIConfig.user.getDoctorOTP + "?phoneno=" + phoneNo;

    return this.apiService.get(url, onResponse, onError);
  }

  verifyDoctorOTP(phoneNo, otp, onResponse, onError) {
    let url = APIConfig.user.verifyDoctorOTP;
    let data = {
      phoneno: parseInt(phoneNo),
      otp: parseInt(otp),
    };

    return this.apiService.post(url, data, onResponse, onError);
  }

  new(employee, onResponse, onError) {
    let url = APIConfig.user.new;

    return this.apiService.post(url, employee, onResponse, onError);
  }

  getEmployeeInfo(employeeId, onResponse, onError) {
    let url = APIConfig.user.get.format(employeeId);

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * The logged-in user's doctor CATEGORY, or 0 when they are not a clinician.
   *
   * `doctorProfile` is the whole doc_profile row (see rxMappers' DOCTOR_KEYS),
   * so this needs no request. An ops-only employee has doctorProfile.id === 0
   * and answers 0 here.
   */
  doctorCategory() {
    const user = this.authenticationService.getUserProfile() || {};
    const dp = user.doctorProfile || {};
    if (!(dp.id > 0)) return 0;
    const c = Number(dp.category);
    return c > 0 ? c : 0;
  }

  /**
   * May this user write a prescription?
   *
   * ONLY category 1. A nutritionist, physiotherapist or psychotherapist has a
   * doctorProfile and can sign into the same screens, so `doctorProfile.id > 0`
   * is NOT the test — it would hand the Rx button to all of them.
   *
   * FAILS CLOSED. A clinician whose category is missing answers false and gets
   * the health-plan button instead. Prescribing is the higher-risk of the two
   * actions, so an unknown category loses the Rx button rather than a
   * nutritionist gaining one.
   */
  isPrescriber() {
    return this.isDoctor();
  }

  /**
   * May this user start a health plan from the patient page? (product,
   * 2026-10-13)
   *
   *   Doctor                        Create Rx, not this
   *   Nutritionist / Physiotherapist
   *   / Medical team                Create health plan
   *   Psychologist (PSYCHOTHERAPIST) neither button
   *
   * Anyone else keeps the health plan, as before: the Rx button fails closed on
   * an unrecognised category, and the health plan has always been its fallback.
   */
  canCreateHealthPlan() {
    return !this.isPrescriber() && this.doctorCategory() !== DoctorCategory.PSYCHOTHERAPIST;
  }

  /**
   * Does this user have a clinical profile AT ALL — of any category?
   *
   * True for a nutritionist and a physiotherapist as much as a doctor, so it is
   * NOT the test for prescribing rights. It is the right test for "can this
   * person be the actor on a clinical record", which is what
   * ReviewPrescription checks (as `doctorProfile.id`) before allowing a
   * prescriber to be resolved at all.
   */
  isClinician() {
    const user = this.authenticationService.getUserProfile() || {};
    const dp = user.doctorProfile || {};
    return dp.id > 0;
  }

  /**
   * Is this user a DOCTOR — category 1, as distinct from a nutritionist,
   * physiotherapist, psychotherapist or medical-team member?
   *
   * WHAT THIS USED TO BE, and why it was wrong: it returned
   * `doctorProfile.id > 0 && !user.employeeProfile`. That asks a different
   * question — "did they sign in through the DOCTOR login rather than the
   * employee one" — which since the move to /doclogin is true of EVERY phr
   * user, so it would answer yes for a nutritionist as readily as for a
   * doctor. Back when phr logged in as an employee it failed the other way,
   * answering no for everyone including real doctors. Wrong in both eras;
   * nothing called it, so nothing broke.
   *
   * It also could not have told a doctor from a nutritionist even where it did
   * work, because both carry a doctorProfile. The category is the only field
   * that separates them.
   *
   * (bridge's copy of this method still has the old body. There the clause is
   * defensible — bridge has a live /doclogin — so it was left alone rather than
   * synced.)
   */
  isDoctor() {
    return this.doctorCategory() === DoctorCategory.DOCTOR;
  }
  /**
   * ALWAYS FALSE under the doctor login: /doclogin returns employeeProfile as
   * null, so there is no id to be greater than zero. Kept because the shape is
   * still right if phr ever regains an employee entry point; it currently has
   * no production callers, only UserService.test.js.
   */
  isEmployee() {
    let isEmp = false;
    const user = this.authenticationService.getUserProfile();
    if (user && user.employeeProfile && user.employeeProfile.id > 0) {
      isEmp = true;
    }
    return isEmp;
  }
  searchDoctorCode(code, onSuccess, onError) {
    const url = APIConfig.user.verifyDocCode.format(code);
    this.apiService.get(url, onSuccess, onError);
  }
}

export default UserService;
